import { useEffect, useRef, useState } from "react"
import { Controller, Resolver, useForm } from "react-hook-form"
import { FiEdit } from "react-icons/fi"
import { HiOutlineRefresh } from "react-icons/hi"
import { IoIosArrowBack } from "react-icons/io"
import { PiQuestion } from "react-icons/pi"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import Bottom from "@fuse/sharedComponents/bottom"
import { EventPluginsRenderer } from "@fuse/sharedComponents/EventPluginsLoader"
import { useAlert } from "@fuse/sharedComponents/globalAlert"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import NextStepsModal from "@fuse/sharedComponents/nextStepsModal"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  EDefaultPayloadData,
  EEVENT_INVITATION_CRITERIA,
  EEventType,
  EFeatureFlags,
  EGENDERS,
  EVENT_CLASS,
  EVENT_STATUS,
} from "@letshang/constants"
import { get, isEmpty } from "lodash-es"
import moment from "moment"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { ROUTES } from "src/App"
import * as yup from "yup"

import { allEventCacheState } from "@fuse/recoil/events"
import { userState } from "@fuse/recoil/signup"
import { stripeUrlState } from "@fuse/recoil/stripe"

import { EFailureMessage } from "@settingsAlias/constants"
import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"

import { createEventWeb, onboardUserToStripe } from "@api/firebaseWrappedApis"
import { useGetAllTickets } from "@api/services/events/useEventsApi"

import {
  convertImageToBase64,
  createEventDefaultDateTime,
  ensureLetsHangHttps,
  safeJsonParse,
} from "@fuse/utils/common"
import { isDev } from "@fuse/utils/DeviceDection"

import { useTimezone } from "@fuse/customHooks/useTimezone"

import { getTheLocationInfo } from "@fuse/pages/explore/utility/locationFunctions"
import DownloadAppModal, {
  DownloadAppModalHandle,
} from "@fuse/pages/landingPage/components/DownloadAppModal"

import Eye from "@fuse/assets/gif/gif-eyes.gif"
import Rocket from "@fuse/assets/gif/gif-rocket.gif"
// Import the default background image
import eventDefaultBg from "@fuse/assets/images/event-default-bg.png"

import DateTimePickerModal, {
  DateTimePickerModalHandle,
} from "../components/dateTimePicker/DateTimePicker"
import EventPluginBottomSheet, {
  EventPluginBottomSheetHandle,
} from "../components/eventPlugins/eventPluginsBottomSheet"
import EventAddAPluginSection from "../components/eventPlugins/eventPluginsSection"
import LocationBottomSheet, {
  LocationBottomSheetHandle,
} from "../components/locationPicker/LocationBottomSheet"
import DescriptionBottomSheet, {
  DescriptionBottomSheetHandle,
} from "../createEvent/DescriptionBottomSheet"
import MoreComponent from "../createEvent/MoreComponent"
import TicketModal from "../createEvent/TicketModal"
import EventDetails from "../EventDetails"
import FastSignUpComponent from "../FastSignUpComponent"
import useFetchEventDetails from "../useFetchEventDetails"
import DescriptionBottomSheetV3 from "./DescriptionBottomSheetV3"
import FullPageModal from "./FullPageModal"
import MoreComponentv3 from "./MoreComponentV3"
import PreviewDetail from "./PreviewDetail"
import PreviewPage from "./PreviewPage"
// import SignUpComponent from "./SignUpComponent"
import WhatHappensAfterGoLive, {
  WhatHappensAfterGoLiveHandle,
} from "./WhatHappensAfterGoLive"

const bgImage = eventDefaultBg

type EventFormData = {
  eventName: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  location: {
    lat: number
    lon: number
    nameOfPlace: string
  }
  description: string
  flyerImage: FileList | null | string
}

const schema = yup
  .object({
    eventName: yup.string().required("Event name is required"),
    startDate: yup.string().required("Start date is required"),
    endDate: yup.string().required("End date is required"),
    startTime: yup.string().required("Start time is required"),
    endTime: yup.string().required("End time is required"),
    location: yup
      .object({
        lat: yup.number().required(),
        lon: yup.number().required(),
        nameOfPlace: yup.string().required(),
      })
      .required("Location is required"),
    description: yup.string().required("Description is required"),
    flyerImage: yup.mixed<FileList | null | string>().nullable(),
  })
  .required()

const CreateEventPage = () => {
  const { initialStartDate, initialStartTime, initialEndDate, initialEndTime } =
    createEventDefaultDateTime()
  const user = useRecoilValue(userState)
  const [stripeUrl, setStripeUrl] = useRecoilState(stripeUrlState)

  // Helper: check if user info is complete (excluding stripe)
  const isProfileComplete =
    user?.email &&
    (user?.firstName || user?.name) &&
    user?.dateOfBirth &&
    (user?.phoneNumber || user?.phone) &&
    user?.emailVerified

  const [searchParams] = useSearchParams()

  const [reDirectedFrom, setReDirectedFrom] = useState("")
  const [flyerFile, setFlyerFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>(bgImage)
  const [isDefaultBg, setIsDefaultBg] = useState(true)
  const [showMore, setShowMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showFastSignUp, setShowFastSignUp] = useState(false)
  const [createdEventId, setCreatedEventId] = useState("")
  const [isCreatingEmptyEvent, setIsCreatingEmptyEvent] = useState(false)
  const [showTicketModal, setShowTicketModal] = useState(false)
  const isIntegrationsEnabled = useProcessFeatureFlags(
    EFeatureFlags.ENABLE_ADD_PLUGIN_TO_EVENT,
  )

  useEffect(() => {
    setReDirectedFrom(searchParams.get("redirectFrom"))
  }, [])

  const [isUploaded, setIsUploaded] = useState({
    flyerImage: false,
    timingAndLocation: false,
  })
  const ticketedEventsWebFeatureFlag = useProcessFeatureFlags(
    EFeatureFlags.TICKETED_EVENTS_WEB,
  )
  const dateTimePickerModalRef = useRef<DateTimePickerModalHandle>(null)
  const onOpenDateTimePickerModal = () => {
    setIsDatePickerModalOpen(true)
    dateTimePickerModalRef.current?.openModal()
  }

  const descriptionBottomSheetRef = useRef<DescriptionBottomSheetHandle>(null)
  const onOpenDescriptionBottomSheet = () => {
    descriptionBottomSheetRef.current?.openModal()
  }

  const { timezone } = useTimezone()
  const navigate = useNavigate()
  const { state, search, key: locationKey } = useLocation()
  const { state: eventState, refresh } = useFetchEventDetails(createdEventId)
  const [cache] = useRecoilState(allEventCacheState)
  const queryParams = new URLSearchParams(search)
  const referenceFrom = queryParams.get("reference")

  const [showNextSteps, setShowNextSteps] = useState(false)
  const [isDatePickerModalOpen, setIsDatePickerModalOpen] = useState(false)
  const [templateState, setTemplateState] = useState(() => {
    const saved = JSON.parse(localStorage.getItem("savedEventDetails") || "{}")
    if (!isEmpty(saved)) localStorage.removeItem("savedEventDetails")
    return !isEmpty(state?.template) ? state?.template : saved
  })

  useEffect(() => {
    if (createdEventId) {
      refresh()
    }
  }, [createdEventId, showMore])

  useEffect(() => {
    if (templateState?.eventId && !templateState?.isTemplate) {
      setCreatedEventId(templateState.eventId)
    }
  }, [templateState])

  // Show force signup modal if user is not logged in
  useEffect(() => {
    if (!user?.isLoggedIn) {
      navigate(
        {
          pathname: ROUTES.SIGNUP,
          search: search,
        },
        {
          state: {
            redirectTo: ROUTES.CREATE_EVENT_V3,
            template: templateState,
          },
        },
      )
      // setShowFastSignUp(true)
    }
  }, [user])

  useEffect(() => {
    if (flyerFile) {
      const objectUrl = URL.createObjectURL(flyerFile)
      setPreviewUrl(objectUrl)
      setIsDefaultBg(false)
      // Cleanup the object URL when component unmounts or file changes
      return () => URL.revokeObjectURL(objectUrl)
    } else if (templateState?.flyerImage) {
      setPreviewUrl(templateState.flyerImage)
      setIsDefaultBg(false)
    } else {
      // Use the default image if no file or templateState flyerImage
      setPreviewUrl(bgImage)
      setIsDefaultBg(true)
      setIsUploaded({
        ...isUploaded,
        flyerImage: false,
      })
      setValue("flyerImage", bgImage)
    }
  }, [flyerFile, templateState])

  const flyerImageUrl = previewUrl

  const countryCode = get(user, "country", "US")
  const [returnURL, setReturnURL] = useState("")

  // Save event details to local storage
  const saveToLocalStore = () => {
    let toBeSavedEventDetails = {}

    if (createdEventId && eventState.eventDetails) {
      toBeSavedEventDetails = {
        ...eventState.eventDetails.eventDocData,
        eventId: createdEventId,
        eventName: formState.eventName,
        eventDescription: formState.description,
      }
    } else {
      toBeSavedEventDetails = { ...formState, flyerImage: "" }
    }

    localStorage.setItem(
      "savedEventDetails",
      JSON.stringify(toBeSavedEventDetails),
    )
  }

  const fetchStripeUrlSilently = async () => {
    if (stripeUrl.isLoading) return
    try {
      setStripeUrl((prev) => ({ ...prev, isLoading: true }))
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.set("redirectFrom", "stripe")
      const returnURL = currentUrl.toString()

      const response = await onboardUserToStripe({
        countryCode: countryCode,
        returnURL: returnURL,
        refreshURL: returnURL,
      })

      const isSuccess = get(response, "success", true)
      if (!isSuccess) return

      const error = get(response, "error", null)
      if (error) return

      const url = get(response, "url", "")
      setStripeUrl(() => ({ isLoading: false, url: url }))
    } catch (e) {
      console.error("Silent stripe URL fetch failed:", e)
      setStripeUrl(() => ({ isLoading: false, url: "" }))
    }
  }
  // Check profile completion and fetch Stripe URL if needed
  useEffect(() => {
    if (user?.stripeAccountId) {
      return
    } // No need to poll if already connected

    const intervalId = setInterval(
      () => {
        fetchStripeUrlSilently()
      },
      4 * 60 * 1000,
    ) // 4 minutes in milliseconds

    // Run once immediately as well
    fetchStripeUrlSilently()

    return () => clearInterval(intervalId) // Cleanup
  }, [])

  // Also check when component mounts or when returning to the page
  // Empty dependency array means this runs once when component mounts

  const onConnectToStripe = async () => {
    if (!isEmpty(stripeUrl.url)) {
      window.open(stripeUrl.url, "_self")
      setStripeUrl((prev) => ({ ...prev, url: "" })) // Clear the URL after use
      return
    }

    try {
      setIsLoading(true)

      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.set("redirectFrom", "stripe")
      const returnURL = currentUrl.toString()

      await onboardUserToStripe({
        countryCode: countryCode,
        returnURL: returnURL,
        refreshURL: returnURL,
      })
        .then((data) => {
          const isSuccess = get(data, "success", true)

          if (!isSuccess) {
            const errorMessage = get(
              data,
              "message",
              EFailureMessage.UserAlreadyConnectWithStripe,
            )

            console.log("error Message", errorMessage, data)
            setIsLoading(false)
            showErrorToast(errorMessage)
            return
          }
          const error = get(data, "error", null)
          if (error) {
            setIsLoading(false)
            showErrorToast(EFailureMessage.UnableToConnectWithStripe)
            return
          }
          setIsLoading(false)

          const stripeURL = get(data, "url", "")
          window.open(stripeURL, "_self")
        })
        .catch(() => {
          setIsLoading(false)
          showErrorToast(EFailureMessage.UnableToConnectWithStripe)
        })
    } catch (e) {
      showErrorToast("Something went wrong.")
      setIsLoading(false)
    }
  }

  function convertToUTC(dateTimeStr: string) {
    return moment(dateTimeStr, "MMMM DD YYYY hh:mm A").utc().toISOString()
  }

  const checkImageType = async (file: FileList | string) => {
    if (typeof file === "string") {
      // Handle URLs
      if (file.startsWith("http")) {
        return file
      }
      // Handle imported assets
      else if (file === bgImage) {
        const response = await fetch(bgImage)
        const blob = await response.blob()
        const base64String = await convertImageToBase64(blob)
        if (typeof base64String === "string") {
          return base64String.split(",")[1]
        }
        // For imported assets, we'll return null to indicate no upload is needed
        // The frontend will use the imported asset directly
        // return "https://firebasestorage.googleapis.com/v0/b/walkme-e7896.appspot.com/o/event%20assets%2F1.png?alt=media&token=a45a8b60-1c42-4cb6-bfef-b78aba693a5c"
      }
      // Handle base64 strings
      else {
        return file.split(",")[1]
      }
    } else {
      // Handle FileList objects
      const base64String = await convertImageToBase64(file)
      if (typeof base64String === "string") {
        return base64String.split(",")[1]
      }
      return ""
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    control,
    watch,
    setValue,
    trigger,
  } = useForm<EventFormData>({
    resolver: yupResolver(schema) as unknown as Resolver<EventFormData>,
    defaultValues: {
      eventName: templateState?.title || templateState?.eventName || "",
      startDate: templateState?.startDate
        ? templateState?.startDate
        : templateState?.timing?.eventDate
          ? moment(templateState?.timing?.eventDate).format("MMMM DD YYYY")
          : initialStartDate,
      endDate: templateState?.endDate
        ? templateState?.endDate
        : templateState?.timing?.eventEndDate
          ? moment(templateState?.timing?.eventEndDate).format("MMMM DD YYYY")
          : initialEndDate,
      startTime: templateState?.startTime
        ? templateState?.startTime
        : templateState?.timing?.eventStartTime
          ? moment(templateState?.timing?.eventStartTime).format("hh:00 A")
          : initialStartTime,
      endTime: templateState?.endTime
        ? templateState?.endTime
        : templateState?.timing?.eventEndTime
          ? moment(templateState?.timing?.eventEndTime).format("hh:00 A")
          : initialEndTime,
      location: {
        lat: templateState?.location?.latitude || 0,
        lon: templateState?.location?.longitude || 0,
        nameOfPlace: templateState?.location?.eventLocation || "",
      },
      description:
        templateState?.description || templateState?.eventDescription || "",
      flyerImage: templateState?.flyerImage || flyerImageUrl,
    },
  })
  // const [signUpModal, setSignUpModal] = useState(false)
  // console.log("signUpModal", signUpModal)
  const formState = watch()
  const endDate = watch("endDate")
  const endTime = watch("endTime")
  const startDate = watch("startDate")
  const startTime = watch("startTime")
  const description = watch("description")
  const location = watch("location")
  const defaultPayloadData = {
    minAge: EDefaultPayloadData.MIN_AGE,
    maxAge: EDefaultPayloadData.MAX_AGE,
    howManyPeople: [
      EDefaultPayloadData.MIN_PEOPLE,
      EDefaultPayloadData.MAX_PEOPLE,
    ],
    numberAttending: EDefaultPayloadData.MIN_PEOPLE,
    numberRemainingSpots: EDefaultPayloadData.MAX_PEOPLE,
    restrictMemberGender: EGENDERS.OPEN,
  }

  const createEventV2ForFlyerImage = async (eventId?: string) => {
    const finalFlyerImage = await checkImageType(formState?.flyerImage)
    const payloadData: any = {
      flyerImage: finalFlyerImage,
    }

    if (createdEventId || eventId) {
      payloadData.eventId = createdEventId || eventId
    }

    if (!isUploaded?.timingAndLocation) {
      payloadData.timing = {
        eventDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndDate: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        timeZone: "UTC",
        eventStartTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndTime: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        signupCloseDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        signupCloseTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        displayTimezone: timezone,
      }
    }
    payloadData.eventClass =
      tickets.length > 0
        ? EVENT_CLASS.TICKETED_EVENT
        : EVENT_CLASS.NON_TICKETED_EVENT

    await createEventWeb({ ...payloadData }).then((res) => {
      if (res.success) {
        setIsUploaded({
          ...isUploaded,
          flyerImage: true,
        })
        if (!createdEventId) {
          setCreatedEventId(res.data.eventId)
        }
      } else if (!res.success) {
        setIsUploaded({ ...isUploaded, flyerImage: false })
      }
    })
  }

  useEffect(() => {
    if (
      previewUrl !== bgImage &&
      !isCreatingEmptyEvent &&
      (!reDirectedFrom || (reDirectedFrom && reDirectedFrom !== "stripe"))
    ) {
      createEventV2ForFlyerImage()
    }
  }, [previewUrl, isCreatingEmptyEvent])

  const handleDateTimeConfirm = ({
    startDate,
    startTime,
    endDate,
    endTime,
  }: {
    startDate: string
    startTime: string
    endDate: string
    endTime: string
  }) => {
    setValue("startDate", startDate, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue("startTime", startTime, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue("endDate", endDate, { shouldValidate: true, shouldDirty: true })
    setValue("endTime", endTime, { shouldValidate: true, shouldDirty: true })
  }

  const locationBottomSheetRef = useRef<LocationBottomSheetHandle>(null)
  const onOpenLocationBottomSheet = () => {
    locationBottomSheetRef.current?.openModal()
  }

  const { data: ticketsData } = useGetAllTickets(createdEventId)
  const tickets = get(ticketsData, "data.tickets", [])

  const onSubmit = async (data: EventFormData) => {
    setIsLoading(true)

    let payloadData: any = {
      eventName: data?.eventName,
      eventDescription: data?.description,
      eventStatus: EVENT_STATUS.OPEN,
      eventClass:
        tickets.length > 0
          ? EVENT_CLASS.TICKETED_EVENT
          : EVENT_CLASS.NON_TICKETED_EVENT,
    }
    if (createdEventId) {
      payloadData.eventId = createdEventId
    }

    if (!isUploaded.flyerImage) {
      const finalFlyerImage = await checkImageType(formState?.flyerImage)
      payloadData.flyerImage = finalFlyerImage
    }

    if (!isUploaded.timingAndLocation) {
      payloadData.timing = {
        eventDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndDate: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        timeZone: "UTC",
        eventStartTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndTime: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        signupCloseDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        signupCloseTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        displayTimezone: timezone,
      }
      payloadData.location = {
        eventLocation: formState?.location?.nameOfPlace,
        latitude: formState?.location?.lat,
        longitude: formState?.location?.lon,
        guestLocationPreview: await getTheLocationInfo({
          latitude: formState?.location?.lat,
          longitude: formState?.location?.lon,
        }),
      }
      payloadData = {
        ...payloadData,
        whoCanJoin: EEVENT_INVITATION_CRITERIA.INVITE_BY_LINK_ONLY,
        eventType: EEventType.FREE_INDIVIDUAL,
        restrictMemberGender: defaultPayloadData.restrictMemberGender,
        numberAttending: defaultPayloadData.numberAttending,
        numberRemainingSpots: defaultPayloadData.numberRemainingSpots,
        minAge: defaultPayloadData.minAge,
        maxAge: defaultPayloadData.maxAge,
        invitationCriteria: EEVENT_INVITATION_CRITERIA.INVITE_BY_LINK_ONLY,
        howManyPeople: defaultPayloadData.howManyPeople,
      }
      if (templateState?.publicEvent) {
        payloadData.publicEvent = templateState.publicEvent
      }
      if (templateState?.category) {
        payloadData.category = templateState.category
      }
    }
    await createEventWeb({ ...payloadData })
      .then(async (res: any) => {
        if (res?.success) {
          const isTicketedEvent = tickets.length > 0
          navigate(
            `${ROUTES.EVENT_CREATE_SUCCESS}?eventId=${res.data.eventId}&ticketed=${isTicketedEvent}`,
            {
              state: {
                eventName: data?.eventName,
                flyerImage: flyerImageUrl,
                eventId: res.data.eventId,
                isTicketedEvent,
              },
              replace: true, // Replace the current history entry
            },
          )
          setIsLoading(false)
        } else {
          showErrorToast(
            res?.message || "Something went wrong, please try again later",
          )
          setIsLoading(false)
        }
      })
      .catch((err) => {
        showErrorToast(
          err?.message || "Something went wrong while saving the draft",
        )
        setIsLoading(false)
      })
  }

  const modalRef = useRef<DownloadAppModalHandle>(null)
  const whatHappensAfterGoLiveRef = useRef<WhatHappensAfterGoLiveHandle>(null)
  const eventPluginBottomSheetRef = useRef<EventPluginBottomSheetHandle>(null)

  const onOpenDownloadAppModal = () => {
    modalRef.current?.openModal()
  }

  const onOpenWhatHappensAfterGoLive = () => {
    console.log("onOpenWhatHappensAfterGoLive")
    whatHappensAfterGoLiveRef.current?.openModal()
  }

  const handleDescriptionChange = (value: string) => {
    setValue("description", value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const handleLocationChange = (value: any) => {
    setValue("location", value.location, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const handleSubmitWithValidation = (isOpen = false) => {
    // if (!user?.isLoggedIn) {
    //   setSignUpModal(true)
    // } else {
    handleSubmit(
      (data) => {
        if (!isOpen) {
          onSubmit(data)
        } else {
          setIsOpen(true)
        }
      },
      (errors) => {
        // This callback runs when there are validation errors
        setIsLoading(false)
        console.log("Form errors:", errors)

        // Generic error message
        showErrorToast("Missing required fields")
      },
    )()
    // }
    // This will run validation before submitting
  }

  // Handler for Tickets button
  const handleTicketsClick = () => {
    setShowMore(false)
    if (!createdEventId && ticketedEventsWebFeatureFlag) {
      setIsCreatingEmptyEvent(true)
      createEmptyEvent()
    }

    // Check if profile is incomplete or Stripe is not connected
    if (!isProfileComplete || !user?.stripeAccountId) {
      setShowNextSteps(true)
    } else {
      setShowTicketModal(true)
    }
  }

  // Handler for Complete Profile arrow
  const handleCompleteProfile = () => {
    setShowNextSteps(false)
    navigate(ROUTES.COMPLETE_PROFILE)
  }

  const createEventV2ForData = async () => {
    let payloadData: any = {}

    if (createdEventId) {
      payloadData.eventId = createdEventId
    }

    if (!isUploaded.timingAndLocation) {
      payloadData = {
        ...payloadData,

        whoCanJoin: EEVENT_INVITATION_CRITERIA.INVITE_BY_LINK_ONLY,
        eventType: EEventType.FREE_INDIVIDUAL,
        eventStatus: EVENT_STATUS.DRAFT,
        restrictMemberGender: defaultPayloadData.restrictMemberGender,
        numberAttending: defaultPayloadData.numberAttending,
        numberRemainingSpots: defaultPayloadData.numberRemainingSpots,
        minAge: defaultPayloadData.minAge,
        maxAge: defaultPayloadData.maxAge,
        invitationCriteria: EEVENT_INVITATION_CRITERIA.INVITE_BY_LINK_ONLY,
        howManyPeople: defaultPayloadData.howManyPeople,
      }
      if (templateState?.publicEvent) {
        payloadData.publicEvent = templateState.publicEvent
      }
      if (templateState?.category) {
        payloadData.category = templateState.category
      }
    }

    payloadData = {
      ...payloadData,
      eventClass:
        tickets.length > 0
          ? EVENT_CLASS.TICKETED_EVENT
          : EVENT_CLASS.NON_TICKETED_EVENT,
      timing: {
        eventDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndDate: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        timeZone: "UTC",
        eventStartTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndTime: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        signupCloseDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        signupCloseTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        displayTimezone: timezone,
      },
      location: {
        eventLocation: formState?.location?.nameOfPlace,
        latitude: formState?.location?.lat,
        longitude: formState?.location?.lon,
        guestLocationPreview: await getTheLocationInfo({
          latitude: formState?.location?.lat,
          longitude: formState?.location?.lon,
        }),
      },
    }

    await createEventWeb({ ...payloadData }).then((res) => {
      if (res.success) {
        setIsUploaded({ ...isUploaded, timingAndLocation: true })
        if (!createdEventId) {
          setCreatedEventId(res.data.eventId)
        }
        if (!isUploaded.flyerImage) {
          createEventV2ForFlyerImage(res.data.eventId)
        }
      } else if (!res.success) {
        setIsUploaded({ ...isUploaded, timingAndLocation: false })
      }
    })
  }
  const hasMounted = useRef(false)

  useEffect(() => {
    if (hasMounted.current) {
      if (
        !isDatePickerModalOpen &&
        (dirtyFields.endDate ||
          dirtyFields.startDate ||
          dirtyFields.endTime ||
          dirtyFields.startTime ||
          dirtyFields.location)
      ) {
        createEventV2ForData()
      }
    } else {
      hasMounted.current = true
    }
  }, [location, isDatePickerModalOpen])

  const [isOpen, setIsOpen] = useState(false)

  const createEmptyEvent = async () => {
    await createEventWeb({
      eventStatus: EVENT_STATUS.DRAFT,
    }).then((res) => {
      if (res.success) {
        setCreatedEventId(res.data.eventId)
        setIsCreatingEmptyEvent(false)
      }
    })
  }

  const { showAlert } = useAlert()

  const onCancelPress = () => {
    showAlert(
      "Are you sure you want to cancel this event?",
      "If you cancel now, all the progress you’ve made on this event will be lost.",
      "Yes, Cancel",
      () => {
        handleCancelPress()
      },
      "Keep Editing",
      () => {},
    )
  }
  const handleCancelPress = () => {
    const canGoBack = locationKey !== "default"

    if (!isEmpty(state?.template)) {
      navigate(`${ROUTES.EVENT_TEMPLATES}?category=${state.category}`)
      return
    }

    if (canGoBack) {
      navigate(-1)
      return
    }

    if (user?.isLoggedIn || isEmpty(referenceFrom)) {
      navigate(ROUTES.LANDING)
    } else {
      window.location.href = ensureLetsHangHttps(referenceFrom)
    }
  }

  const flyerInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    navigate(ROUTES.CREATE_EVENT_V5)
  }, [])

  if (showMore) {
    return (
      <MoreComponent
        flyerImageUrl={flyerImageUrl}
        onBack={() => setShowMore(false)}
        eventId={createdEventId}
        onTicketsClick={handleTicketsClick}
      />
    )
  }

  return isOpen ? (
    <PreviewPage
      isDefaultBg={isDefaultBg}
      flyerImageUrl={flyerImageUrl}
      formState={formState}
      user={user}
      timezone={timezone}
      onClose={() => setIsOpen(false)}
    />
  ) : (
    <div className="relative max-h-screen overflow-hidden">
      <BackgroundWrapper
        showImage={!isDefaultBg}
        bgImage={flyerImageUrl}
        className={`max-h-[100vh] h-full overflow-auto touch-manipulation ${
          isDefaultBg ? "!bg-gradient-to-b from-[#F1C2DB] to-[#4F5293]" : ""
        }`}
      >
        {isLoading && <LoadingSpinner />}
        <BlurWrapper
          blurKey={get(blurKeys, "mainBlurView")}
          overlayOpacityDefault={isDefaultBg ? "0" : "75"}
          overlayColorDefault="dark"
          className={`${isDefaultBg ? "bg-black/50" : "bg-gray-400/10"} h-[10vh] flex flex-row  items-center px-6  top-0 left-0 w-full z-10 fixed`}
        >
          <div
            className="text-white text-base cursor-pointer bg-white/10 h-12 w-12 rounded-full flex justify-center items-center"
            onClick={onCancelPress}
          >
            <IoIosArrowBack />
          </div>

          <div className="text-white text-base cursor-pointer flex-1">
            Create event
          </div>

          <div
            className="text-white text-base cursor-pointer bg-white/10 h-12 w-12 rounded-full flex justify-center items-center"
            onClick={onOpenWhatHappensAfterGoLive}
          >
            <PiQuestion size={20} />
          </div>
        </BlurWrapper>

        <BlurWrapper
          blurKey={get(blurKeys, "mainBlurView")}
          overlayOpacityDefault={isDefaultBg ? "0" : "75"}
          overlayColorDefault="dark"
          className="min-h-[100vh] h-full flex flex-col overflow-auto relative"
        >
          <div className="flex flex-col justify-center items-center p-6 pb-36 w-full max-w-2xl mx-auto mt-[10vh]">
            <form
              className="w-full space-y-6 flex flex-col justify-center items-center"
              onSubmit={(e) => {
                e.preventDefault()
                handleSubmitWithValidation()
              }}
            >
              <div className="space-y-2 relative ">
                <img
                  src={flyerImageUrl}
                  alt="Flyer"
                  className="h-52 w-56 rounded-lg object-cover border-2 border-white/20 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    flyerInputRef.current?.click()
                  }}
                />

                <div className="absolute w-full bottom-2 right-2 flex flex-row justify-end gap-2">
                  {!isDefaultBg && (
                    <BlurWrapper
                      overlayOpacityDefault={isDefaultBg ? "0" : "30"}
                      overlayColorDefault="dark"
                      className={`h-10 w-10 rounded-full flex justify-center items-center   ${
                        isDefaultBg ? "!bg-black/50 !backdrop-blur-sm" : ""
                      }`}
                      onClick={() => {
                        setReDirectedFrom(null)
                        setFlyerFile(null)

                        flyerInputRef.current?.click()
                      }}
                    >
                      <div>
                        <HiOutlineRefresh className="text-white text-lg" />
                      </div>
                    </BlurWrapper>
                  )}
                  <BlurWrapper
                    overlayOpacityDefault={isDefaultBg ? "0" : "30"}
                    overlayColorDefault="dark"
                    className={`h-10 w-10 rounded-full flex justify-center items-center   ${
                      isDefaultBg ? "!bg-black/50 !backdrop-blur-sm" : ""
                    }`}
                    onClick={() => {
                      setReDirectedFrom(null)
                      flyerInputRef.current?.click()
                    }}
                  >
                    <div>
                      <FiEdit className="text-white text-lg" />
                    </div>
                    <input
                      id="flyerImageInput"
                      type="file"
                      accept="image/jpeg,image/png"
                      {...register("flyerImage")}
                      ref={flyerInputRef}
                      onClick={() => {
                        if (!createdEventId) {
                          setIsCreatingEmptyEvent(true)
                          createEmptyEvent()
                        }
                      }}
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null
                        if (file) {
                          const fileType = file.type.toLowerCase()
                          if (
                            fileType !== "image/jpeg" &&
                            fileType !== "image/png"
                          ) {
                            alert("Please select only JPG or PNG images")
                            e.target.value = ""
                            return
                          }
                        }
                        setFlyerFile(file)
                        setValue("flyerImage", e.target.files[0] as any)
                      }}
                      className="hidden"
                    />
                  </BlurWrapper>
                </div>
              </div>
              <div
                className={`space-y-2 ${
                  isDefaultBg ? "bg-black/50" : "bg-white/10"
                } w-full rounded-2xl p-4 ${errors.eventName ? "border-2 border-red-400" : ""}`}
              >
                <input
                  {...register("eventName")}
                  className="w-full rounded-lg bg-transparent text-white focus:outline-none text-2xl mb-3"
                  placeholder="Name your event"
                />

                {user?.isLoggedIn ? (
                  <>
                    <hr className="w-full border-white/30 pb-3" />
                    <div className="flex flex-row gap-4 items-center">
                      <img
                        src={user?.avatarUrl}
                        alt="Flyer"
                        className="h-8 w-8 rounded-full"
                      />
                      <span className="flex flex-col justify-center items-center text-base text-white/50">
                        by {user?.name}
                      </span>
                    </div>
                  </>
                ) : null}
              </div>

              <div
                className={`space-y-2 ${
                  isDefaultBg ? "bg-black/50" : "bg-white/10"
                } w-full rounded-2xl p-4 ${errors.startDate || errors.endDate || errors.location ? "border-2 border-red-400" : ""}`}
              >
                <div
                  className="flex flex-row gap-4 items-center py-2"
                  onClick={onOpenDateTimePickerModal}
                >
                  <p className="text-2xl">🗓️</p>

                  <input
                    type="text"
                    className="w-full rounded-lg bg-transparent text-white  focus:outline-none"
                    placeholder="Date and time"
                    value={`${startDate}, ${startTime}`}
                  />
                </div>
                <hr className="w-full  border-white/30" />
                {/*----------------Location Picker----------------*/}
                <div className="">
                  <div className={`flex rounded-xl w-full`}>
                    <Controller
                      name="location"
                      control={control}
                      render={({ field }) => (
                        <div
                          className="flex py-2 items-center  w-full"
                          onClick={() => {
                            onOpenLocationBottomSheet()
                          }}
                        >
                          <div className="">
                            <p className="text-2xl">📍</p>
                          </div>
                          <div className=" ml-2 flex flex-1 items-center w-full">
                            <div className="flex-1 w-full">
                              <div className="flex justify-between w-full">
                                <input
                                  className="bg-transparent w-full focus:outline-none text-white ml-2 text-ellipsis "
                                  placeholder="Location"
                                  value={location?.nameOfPlace || ""}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div
                className={`space-y-2 w-full rounded-2xl p-4 ${
                  isDefaultBg ? "bg-black/50" : "bg-white/10"
                } ${errors.description ? "border-2 border-red-400" : ""}`}
                onClick={onOpenDescriptionBottomSheet}
              >
                <textarea
                  // {...register("description")}
                  className="w-full  bg-transparent text-white min-h-[200px] text-base focus:outline-none resize-none"
                  placeholder="Describe your event"
                  value={description}
                />
              </div>

              {/* Plugins Section */}
              {isIntegrationsEnabled && (
                <div className="space-y-4 w-full">
                  {/* Show existing plugins if event exists */}
                  {createdEventId &&
                    (() => {
                      // Use cache data instead of global event state to avoid showing plugins from previous events
                      const cachedEventData = cache[createdEventId]
                      const eventDocData =
                        cachedEventData?.eventDetails?.eventDocData

                      return eventDocData ? (
                        <EventPluginsRenderer
                          eventDocData={eventDocData}
                          eventId={createdEventId}
                          user={user}
                          isAdmin={true}
                          unCachedLocation={{}}
                        />
                      ) : null
                    })()}

                  {/* Always show add plugins section */}
                  <EventAddAPluginSection
                    eventId={createdEventId}
                    bottomSheetRef={eventPluginBottomSheetRef}
                    onEventCreated={(eventId) => {
                      setCreatedEventId(eventId)
                    }}
                    backgroundStyle={{
                      containerClassName: `space-y-4 w-full rounded-2xl p-4 cursor-pointer ${
                        isDefaultBg ? "bg-black/50" : "bg-white/10"
                      }`,
                      searchClassName: `flex gap-2 items-center justify-center bg-white/10 rounded-lg p-3 w-full ${
                        isDefaultBg ? "bg-white/10" : "bg-black/25"
                      }`,
                    }}
                    user={user}
                  />
                </div>
              )}
            </form>

            <MoreComponentv3
              isDefaultBg={isDefaultBg}
              eventId={createdEventId}
              onTicketsClick={handleTicketsClick}
              onOpenDownloadAppModal={onOpenDownloadAppModal}
            />
          </div>
        </BlurWrapper>

        <div className=" px-6 w-full max-w-xl mb-6  fixed bottom-0  -translate-x-1/2 left-1/2 z-[110]">
          <BlurWrapper
            blurKey={get(blurKeys, "mainBlurView")}
            overlayOpacityDefault="20"
            blurIntensityDefault={"sm"}
            overlayColorDefault="light"
            className="overflow-hidden rounded-2xl "
          >
            <div className="flex overflow-hidden  gap-5">
              <div
                className="flex flex-col p-5 items-center flex-grow hover:cursor-pointer"
                onClick={() => {
                  handleSubmitWithValidation(true)
                }}
              >
                <img src={Eye} width={20} height={20} />
                <span className="text-white">Preview</span>
              </div>

              <div
                className="flex flex-col p-5 items-center flex-grow hover:cursor-pointer"
                onClick={() => {
                  handleSubmitWithValidation(false)
                }}
              >
                <img src={Rocket} width={20} height={20} />
                <span className="text-green-500">Go live</span>
              </div>
            </div>
          </BlurWrapper>
        </div>

        <DateTimePickerModal
          ref={dateTimePickerModalRef}
          startDate={startDate}
          startTime={startTime}
          endDate={endDate}
          endTime={endTime}
          onConfirm={handleDateTimeConfirm}
          setIsDatePickerModalOpen={setIsDatePickerModalOpen}
          isDefaultBg={isDefaultBg}
        />

        <DescriptionBottomSheetV3
          ref={descriptionBottomSheetRef}
          register={register}
          errors={errors}
          description={description}
          onChange={handleDescriptionChange}
          isDefaultBg={isDefaultBg}
          eventName={formState.eventName}
          date={moment
            .tz(`${formState.startDate}, ${formState.startTime}`, timezone)
            .format("DD MMM YYYY, hh:mm A z")}
          location={formState.location?.nameOfPlace}
        />

        <LocationBottomSheet
          onOpenAdvancedSettings={onOpenDownloadAppModal}
          ref={locationBottomSheetRef}
          value={{ location: location }}
          onLocationSelected={handleLocationChange}
          isDefaultBg={isDefaultBg}
        />
        <DownloadAppModal ref={modalRef} />
        <FastSignUpComponent
          isOpen={showFastSignUp}
          setOpenState={setShowFastSignUp}
          title="Let's get you started"
          showTitleAsIs={true}
          cannotDismissPopup={true}
        />
        <NextStepsModal
          isOpen={showNextSteps}
          onClose={() => setShowNextSteps(false)}
          onCompleteProfile={handleCompleteProfile}
          onConnectToStripe={onConnectToStripe}
          onClickAddTickets={() => {
            if (!isProfileComplete) {
              setShowTicketModal(true)
            }
          }}
          saveToLocal={saveToLocalStore}
        />
        <TicketModal
          isOpen={showTicketModal}
          onClose={() => setShowTicketModal(false)}
          eventId={createdEventId}
        />
        <WhatHappensAfterGoLive ref={whatHappensAfterGoLiveRef} />
        <EventPluginBottomSheet
          ref={eventPluginBottomSheetRef}
          eventId={createdEventId}
          onEventCreated={(eventId) => {
            setCreatedEventId(eventId)
          }}
        />
      </BackgroundWrapper>
    </div>
  )
}

export default CreateEventPage
